// Dark Theme Color Palette
// These colors are used across the application including Material-UI theme and ReactFlow.
// Button defaults (text/background) are theme-specific via the optional `button.primary` token.
// IMPORTANT: Keep these values in sync with defaultColors.scss

export const darkMainColors = {
	mode: 'dark',
	// Primary Colors
	primary: {
		main: '#00B8B0', // Light blue for dark mode
	},
	secondary: {
		main: '#7d6e65', // Warm gray for left menu
		dark: '#3d3935',
	},
	// Background Colors
	background: {
		default: '#1a1a1a', // Very dark background
		canvas: '#1a1a1a',
		gutter: '#494949',
		gutterGradientTo: '#5a5a5a',
		paper: '#2a2a2a', // Slightly lighter for cards/panels
		header: '#494949',
		texture: 'none',
	},
	surface: {
		elevationOverlay: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))',
	},
	// Text Colors
	text: {
		primary: '#e0e0e0', // Light gray text
		secondary: '#b0b0b0', // Dimmer gray for secondary text
	},
	button: {
		primary: {
			background: '#00B8B0',
			text: '#FFFFFF',
		},
	},
} as const;

/** Navy palette derived from the supplied Randol blue design direction. */
export const darkBlueColors = {
	mode: 'dark',
	primary: { main: '#168BFF', light: '#BFEAFF' },
	// The navigation is intentionally lighter than the canvas, keeping it visually distinct.
	secondary: { main: '#102947', dark: '#0B203A' },
	background: { default: '#071526', canvas: '#071526', gutter: '#071526', gutterGradientTo: '#113155', paper: '#0C1D33', header: '#0C1D33', texture: 'none' },
	text: { primary: '#E5EDFF', secondary: '#AFC0DC' },
	button: { primary: { background: '#168BFF', text: '#FFFFFF' } },
	surface: { elevationOverlay: 'linear-gradient(rgba(191, 234, 255, 0.05), rgba(191, 234, 255, 0.05))' },
} as const;

/** Bright workspace surface with the Randol blue accent system. */
export const whiteBlueColors = {
	mode: 'light',
	primary: { main: '#168BFF', light: '#BFEAFF' },
	// Cool gray-blue surfaces prevent the light workspace from feeling stark while
	// preserving enough contrast for cards, tables, and input controls.
	secondary: { main: '#E3ECF7', dark: '#D9E5F3' },
	background: {
		default: '#EDF2F7',
		canvas: '#F1F6FA',
		gutter: '#F1F6FA',
		gutterGradientTo: '#FFFFFF',
		paper: '#F8FAFC',
		header: '#F8FAFC',
		texture:
			'radial-gradient(circle at 18% 12%, rgba(22, 139, 255, 0.07) 0%, transparent 36%), radial-gradient(circle at 82% 10%, rgba(191, 234, 255, 0.2) 0%, transparent 30%)',
	},
	text: { primary: '#10213A', secondary: '#52657F' },
	button: { primary: { background: '#168BFF', text: '#FFFFFF' } },
	surface: { elevationOverlay: 'none' },
} as const;

/** Warm editorial workspace surfaces with Randol blue retained as the product accent. */
export const lightWarmColors = {
	mode: 'light',
	// Anthropic-inspired editorial neutrals, with a softened Randol blue kept
	// for product actions and the octopus mark.
	primary: { main: '#4A7FA3', light: '#C2D6E2' },
	secondary: { main: '#E8E2D8', dark: '#DED5C8' },
	background: {
		default: '#F7F6F2',
		canvas: '#F9F7F3',
		gutter: '#F9F7F3',
		gutterGradientTo: '#FFFEFC',
		paper: '#FFFEFA',
		header: '#FFFEFA',
		texture:
			'radial-gradient(circle at 16% 11%, rgba(74, 127, 163, 0.08) 0%, transparent 36%), radial-gradient(circle at 84% 9%, rgba(232, 226, 216, 0.45) 0%, transparent 30%)',
	},
	text: { primary: '#1F1D1A', secondary: '#6B655D' },
	button: { primary: { background: '#4A7FA3', text: '#FFFFFF' } },
	surface: { elevationOverlay: 'none' },
} as const;

/** Warm neutral surfaces with a crisp Randol blue action system. */
export const randolWarmColors = {
	mode: 'light',

	// Randol brand blue.
	// Used as a crisp accent rather than as a dominant surface colour.
	primary: {
		main: '#2869C7',
		dark: '#1E56A4',
		light: '#6E97D8',
		subtle: '#EAF2FD',
		on: '#FFFFFF',
	},

	// Warm neutral family used throughout the shell.
	// The UI should feel cream/parchment rather than grey.
	secondary: {
		main: '#EDE7DC',
		dark: '#EDE7DC',
		light: '#F6F2EB',
		subtle: '#F3EEE9',
	},

	// Main visual characteristic of this theme:
	// sidebar and page share effectively the same warm background.
	background: {
		default: '#F7F5F0',
		canvas: '#F8F6F1',
		gutter: '#F8F6F1',
		gutterGradientTo: '#FFFEFB',
		texture:
			'radial-gradient(circle at 16% 11%, rgba(40, 105, 199, 0.07) 0%, transparent 36%), radial-gradient(circle at 84% 10%, rgba(237, 231, 220, 0.65) 0%, transparent 30%)',
		sidebar: '#EDE7DC',
		paper: '#FCFBF8',
		header: '#FCFBF8',
		card: '#FCFBF8',
		elevated: '#FFFFFF',
		subtle: '#F8F4F0',
		hover: '#F4F7FB',
	},

	// Warm near-black rather than a blue-black.
	text: {
		primary: '#292824',
		secondary: '#716E67',
		tertiary: '#77716B',
		muted: '#96908A',
		disabled: '#B9B3AD',
		inverse: '#FFFFFF',
	},

	// Fine, quiet structure.
	// The sidebar is separated from the main canvas using border/divider,
	// not a different background colour.
	border: {
		default: '#DDD8CF',
		subtle: '#EEE9E4',
		strong: '#D3CDC6',
		active: '#AFC9EA',
		focus: '#79AEEF',
	},

	divider: {
		default: '#DDD8CF',
		subtle: '#F0ECE8',
		sidebar: '#DDD8D2',
	},

	// Navigation-specific colours.
	navigation: {
		background: '#F7F5F0',
		item: '#5B5650',
		itemHover: '#FCFAF6',
		selectedBackground: '#EAF2FD',
		selectedText: '#2869C7',
		selectedIcon: '#4A7FA3',
		sectionLabel: '#8B857E',
	},

	// Cards are intentionally only fractionally different from the page.
	card: {
		background: '#FCFBF8',
		backgroundHover: '#FFFFFF',
		border: '#DDD8CF',
		divider: '#ECE8E3',
		iconBackground: '#EDF3FB',
		shadow: '0 1px 2px rgba(33, 24, 18, 0.025)',
	},

	// Inputs and controls follow the same restrained visual language.
	input: {
		background: '#FFFCFA',
		border: '#DCD7D1',
		borderHover: '#C8C2BB',
		borderFocus: '#79AEEF',
		placeholder: '#9A948E',
	},

	// Semantic colours are slightly softened
	// so they do not fight with the cream foundation.
	success: {
		main: '#15965A',
		dark: '#107344',
		subtle: '#E8F7EF',
	},

	warning: {
		main: '#D98716',
		dark: '#A9630F',
		subtle: '#FFF4E5',
	},

	error: {
		main: '#D84A46',
		dark: '#AA3431',
		subtle: '#FDECEB',
	},

	info: {
		main: '#2687C8',
		dark: '#17689D',
		subtle: '#EAF4FA',
	},

	// Multi-series usage charts.
	// Blue leads, while remaining lines stay softer and less saturated.
	chart: {
		blue: '#1672E8',
		violet: '#7861D8',
		teal: '#299A92',
		sand: '#D7A76A',
		slate: '#74808A',
		taupe: '#B0A297',
		muted: '#D2CCC6',
		grid: '#EEEAE5',
		axis: '#77716B',
	},

	// Subtle blue interaction states for navigation and controls.
	interaction: {
		hover: '#F4F7FB',
		selected: '#DCE6EF',
		selectedStrong: '#DCEBFA',
		pressed: '#D5E6F8',
		focusRing: '#79AEEF',
	},

	// Common icon treatments used throughout the dashboard.
	icon: {
		primary: '#174E8C',
		secondary: '#50555A',
		muted: '#8F9396',
		background: '#EDF3FB',
	},

	// Small status indicators.
	status: {
		active: '#1FA66B',
		warning: '#E08A19',
		error: '#D84A46',
		info: '#2687C8',
		neutral: '#858B91',
	},
	button: {
		primary: {
			background: '#2869C7',
			text: '#FFFFFF',
		},
	},

	surface: {
		elevationOverlay: 'none',
	},
} as const;

/** Teal-and-terracotta palette drawn directly from the Randol brand guide. */
export const randolBrandedColors = {
	mode: 'light',

	// Randol brand teal — the signature logo and primary action colour.
	primary: {
		main: '#00B8B0',
		dark: '#006D6B',
		light: '#5DD6CF',
		subtle: '#E3F7F5',
		on: '#FFFFFF',
		contrastText: '#FFFFFF',
	},

	// Terracotta is the companion accent family for secondary actions and highlights.
	secondary: {
		main: '#F25B3A',
		dark: '#CB4428',
		light: '#F7A38B',
		subtle: '#FFE7DF',
	},

	// Keep shell surfaces mostly white/off-white, with warm sand used on smaller
	// paper-like surfaces and cards to preserve the brand's neutral foundation.
	background: {
		default: '#FFFFFF',
		canvas: '#ced5d3',
		gutter: '#FBF7F2',
		gutterGradientTo: '#FBF7F2',
		texture:
			'radial-gradient(circle at 16% 10%, rgba(0, 184, 176, 0.08) 0%, transparent 38%), radial-gradient(circle at 84% 12%, rgba(242, 91, 58, 0.08) 0%, transparent 34%)',
		sidebar: '#FFFFFF',
		paper: '#FFFFFF',
		header: '#FBF7F2',
		card: '#F4E6D6',
		elevated: '#FFFFFF',
		subtle: '#FBF7F2',
		hover: '#EAF8F8',
	},

	// Ink anchors text and doubles as the brand's 2% dark accent.
	text: {
		primary: '#172A2A',
		secondary: '#4F6361',
		tertiary: '#5C726F',
		muted: '#7E918E',
		disabled: '#AFC0BD',
		inverse: '#FFFFFF',
	},

	border: {
		default: '#DFE7E5',
		subtle: '#ECF2F0',
		strong: '#C9D8D5',
		active: '#8FD9D1',
		focus: '#00B8B0',
	},

	divider: {
		default: '#DFE7E5',
		subtle: '#EEF4F2',
		sidebar: '#EAEFEF',
	},

	navigation: {
		background: '#FFFFFF',
		item: '#2D4443',
		itemHover: '#F5FBFB',
		selectedBackground: '#DFF5F4',
		selectedText: '#006D6B',
		selectedIcon: '#006D6B',
		sectionLabel: '#78908D',
	},

	card: {
		background: '#F4E6D6',
		backgroundHover: '#FBF7F2',
		border: '#DFE7E5',
		divider: '#ECF2F0',
		iconBackground: '#E3F7F5',
		shadow: '0 1px 2px rgba(23, 42, 42, 0.06)',
	},

	input: {
		background: '#FFFFFF',
		border: '#DFE7E5',
		borderHover: '#BFD4D0',
		borderFocus: '#00B8B0',
		placeholder: '#8A9A97',
	},

	success: {
		main: '#2E9B6F',
		dark: '#1F7A56',
		subtle: '#E6F6EE',
	},

	warning: {
		main: '#F25B3A',
		dark: '#CB4428',
		subtle: '#FFEFE9',
	},

	error: {
		main: '#E14A2E',
		dark: '#B73B24',
		subtle: '#FDEEE9',
	},

	info: {
		main: '#2687C8',
		dark: '#17689D',
		subtle: '#EAF4FA',
	},

	// Teal leads the series; terracotta and sand provide warm contrast.
	chart: {
		blue: '#00B8B0',
		violet: '#7861D8',
		teal: '#006D6B',
		sand: '#F25B3A',
		slate: '#74808A',
		taupe: '#F7A38B',
		muted: '#D2CCC6',
		grid: '#ECEAE6',
		axis: '#5C726F',
	},

	interaction: {
		hover: '#E3F7F5',
		selected: '#BCEDEE',
		selectedStrong: '#9AE0D9',
		pressed: '#A7E3DD',
		focusRing: '#00B8B0',
	},

	icon: {
		primary: '#006D6B',
		secondary: '#F25B3A',
		muted: '#8F9396',
		background: '#E3F7F5',
	},

	status: {
		active: '#2E9B6F',
		warning: '#F25B3A',
		error: '#E14A2E',
		info: '#2687C8',
		neutral: '#858B91',
	},
	button: {
		primary: {
			background: '#00B8B0',
			text: '#FFFFFF',
		},
	},

	surface: {
		elevationOverlay: 'none',
	},
} as const;

// Default static palette used by the legacy SCSS and React Flow helpers.
export const themeColors = darkMainColors;
